/**
 * The one place that narrows an unknown thrown value: ApiError (typed
 * backend failure) vs network TypeError vs anything else. `ErrorAlert`
 * renders the result; non-component callers use it directly.
 */
import { ApiError } from "./api";
import i18n from "../i18n";

export type DescribedError = {
  kind: "api" | "network" | "unknown";
  message: string | null;
};

export function describeError(error: unknown): DescribedError {
  if (error instanceof ApiError) {
    if (error.status === 423) {
      // someone else edits this customer: viewing mode, not a failure
      const holder = (error.body as { holder?: string | null } | undefined)?.holder;
      return { kind: "api", message: i18n.t("customer.readOnlyAction", { name: holder ?? "" }) };
    }
    return {
      kind: "api",
      message: error.body?.errors?.[0] ?? error.body?.detail ?? error.message,
    };
  }
  if (error instanceof TypeError) {
    // fetch() rejects with TypeError when the network / server is unreachable
    return { kind: "network", message: null };
  }
  return {
    kind: "unknown",
    message: error instanceof Error ? error.message : null,
  };
}

/** Flat user-facing text for contexts that cannot render an <ErrorAlert>. */
export function errorText(error: unknown): string {
  const described = describeError(error);
  return (
    described.message ??
    (described.kind === "network" ? i18n.t("errors.network") : i18n.t("errors.unknown"))
  );
}
